import React from 'react'

import {isEmpty} from 'es-toolkit/compat'
import {FormikErrors, useFormik} from 'formik'
import {useLocation, useNavigate} from 'react-router'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {signIn, signOut} from '../../redux/features/auth'

import useKeyPress from '../../../../commons/src/hooks/use-key-press'
import * as validator from '../../../../commons/src/libs/validator'
import * as authService from '../../../../commons/src/services/auth'
import preventDoubleClick from '../../../../commons/src/services/prevent-double-click'
import * as userApi from '../../services/api/user'
import {FromLocationState, goToIndex, goToResetPassword} from '../../services/navigation'

import {ExtendedCognitoUserWithPermission} from '../../types'

import emailIcon from '../../../../commons/src/assets/icons/email.svg'
import lockIcon from '../../../../commons/src/assets/icons/lock.svg'

import Button from '../../../../commons/src/components/button'
import FieldNote from '../../../../commons/src/components/field-note'
import FormRow from '../../../../commons/src/components/form-row'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import InputField from '../../../../commons/src/components/input-field'
import Link from '../../../../commons/src/components/link'
import LinkButton from '../../../../commons/src/components/link-button'
import List from '../../../../commons/src/components/list'
import Paragraph from '../../../../commons/src/components/paragraph'
import PasswordInputField from '../../../../commons/src/components/password-input-field'
import Tooltip from '../../../../commons/src/components/tooltip'
import TooltipHandler from '../../../../commons/src/components/tooltip-handler'

import AuthLayoutPartial from './auth-layout-partial'

const SignInPage = () => {
  const dispatch = useAppDispatch()
  const isAuthenticated = useAppSelector(state => !!state.auth.user)
  const isAdminMode = useAppSelector(state => state.auth.isAdminMode)

  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as undefined | FromLocationState

  const [isFirstTimeUser, setIsFirstTimeUser] = React.useState(false)

  // Redirect if user is authenticated
  React.useEffect(() => {
    if (isAuthenticated && !isAdminMode) {
      if (locationState?.from) {
        navigate(locationState.from, {replace: true})
      } else {
        goToIndex(navigate, {replace: true})
      }
    } else if (isAuthenticated && isAdminMode) {
      dispatch(signOut())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isAdminMode])

  const getUserWithPermissionsAndProviderId = async () => {
    const currentUser = await authService.getCurrentAuthUser()
    const userAttributes = await authService.getUserAttributes()
    const userId = userAttributes.sub

    const {permissions, provider} = await userApi.getUserPermissions(userId as string)

    const userWithPermission: ExtendedCognitoUserWithPermission = {
      ...currentUser,
      attributes: userAttributes,
      permission: permissions,
    }
    return {userWithPermission, providerId: provider || undefined}
  }

  const formikSignIn = useFormik({
    initialValues: {
      email: '',
      password: '',
      // Additional errors
      notAuthorized: false,
      general: false,
    },
    validate: values => {
      const errors: FormikErrors<typeof values> = {}

      if (!validator.combineValidators(validator.required, validator.email)(values.email)) {
        errors.email = 'true'
      }

      if (!validator.required(values.password)) {
        errors.password = 'true'
      }

      return errors
    },
    onSubmit: (values, actions) => {
      return authService
        .authSignIn(values.email, values.password)
        .then(async signInOutput => {
          if (signInOutput.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
            setIsFirstTimeUser(true)
          } else {
            const {userWithPermission, providerId} = await getUserWithPermissionsAndProviderId()
            if (!providerId) {
              throw new Error('Sign in failed (incorrect permissions)')
            }
            dispatch(signIn({user: userWithPermission, providerId}))
          }
        })
        .catch(error => {
          if (error.name === 'NotAuthorizedException') {
            actions.setFieldError('notAuthorized', 'true')
          } else {
            actions.setFieldError('general', error.message)
          }
          dispatch(signOut())
        })
    },
  })

  const formikForcePassword = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
      // Additional errors
      general: false,
    },
    validate: values => {
      const errors: FormikErrors<typeof values> = {}

      if (!validator.required(values.password)) {
        errors.password = 'true'
      }

      if (!validator.required(values.confirmPassword)) {
        errors.confirmPassword = 'true'
      }

      return errors
    },
    onSubmit: (values, actions) => {
      if (values.password !== values.confirmPassword) {
        actions.setFieldError('general', 'Please make sure the provided passwords are equal.')
        actions.setFieldError('password', 'true')
        actions.setFieldError('confirmPassword', 'true')
        return Promise.resolve()
      }

      return authService
        .completeNewPassword(values.password)
        .then(async () => {
          const {userWithPermission, providerId} = await getUserWithPermissionsAndProviderId()
          if (!providerId) {
            throw new Error('Sign in failed (incorrect permissions)')
          }
          return dispatch(signIn({user: userWithPermission, providerId}))
        })
        .catch(error => {
          if (
            (error.name === 'InvalidParameterException' ||
              error.name === 'InvalidPasswordException') &&
            error.message.match(/password/gi)
          ) {
            actions.setFieldError('password', 'true')
            actions.setFieldError('confirmPassword', 'true')
            actions.setFieldError(
              'general',
              'Your password must have a minimum length of 8 characters and contain a combination of upper-case, lower-case and digits.'
            )
          } else {
            actions.setFieldError('general', error.message)
          }
        })
    },
  })

  useKeyPress(
    preventDoubleClick(() => {
      if (!isFirstTimeUser) {
        formikSignIn.handleSubmit()
      } else {
        formikForcePassword.handleSubmit()
      }
    }),
    'Enter'
  )

  return (
    <AuthLayoutPartial>
      <Headline label="Partner Portal" className="u-align-center" />
      {!isFirstTimeUser && (
        <>
          <form onSubmit={formikSignIn.handleSubmit}>
            {formikSignIn.errors.general && (
              <Paragraph error>{formikSignIn.errors.general}</Paragraph>
            )}
            {formikSignIn.errors.notAuthorized && (
              <Paragraph error>
                We can&apos;t find an account matching your sign in information or the provided
                password is wrong.
              </Paragraph>
            )}
            <FormRow>
              <InputField
                name="email"
                placeholder="E-Mail"
                icon={<Icon source={emailIcon} />}
                size="200"
                type="email"
                autoFocus
                appearance={
                  formikSignIn.submitCount > 0 && !!formikSignIn.errors.email ? 'error' : undefined
                }
                onChange={(v, n, event) => formikSignIn.handleChange(event)}
                value={formikSignIn.values.email}
                onBlur={formikSignIn.handleBlur}
                disabled={formikSignIn.isSubmitting}
              />
            </FormRow>
            <FormRow>
              <PasswordInputField
                name="password"
                placeholder="Password"
                icon={<Icon source={lockIcon} />}
                size="200"
                appearance={
                  formikSignIn.submitCount > 0 && !!formikSignIn.errors.password
                    ? 'error'
                    : undefined
                }
                onChange={(v, n, event) => formikSignIn.handleChange(event)}
                value={formikSignIn.values.password}
                onBlur={formikSignIn.handleBlur}
                disabled={formikSignIn.isSubmitting}
              />
            </FormRow>
          </form>
          <Link onClick={() => goToResetPassword(navigate, {locationState, replace: true})}>
            Forgot password?
          </Link>
          <Button
            as="a"
            block
            size="200"
            className="u-margin-top-l"
            disabled={
              formikSignIn.isSubmitting ||
              (!isEmpty(formikSignIn.errors) && formikSignIn.submitCount > 0)
            }
            onClick={preventDoubleClick(() => formikSignIn.handleSubmit())}
          >
            Continue
          </Button>
        </>
      )}
      {isFirstTimeUser && (
        <>
          <form onSubmit={formikForcePassword.handleSubmit}>
            {!formikForcePassword.errors.general && (
              <Paragraph>This is your first login. Please set a new password.</Paragraph>
            )}
            {formikForcePassword.errors.general && (
              <Paragraph error>{formikForcePassword.errors.general}</Paragraph>
            )}
            <FormRow>
              <FieldNote
                note={
                  <TooltipHandler
                    openOnClick
                    tooltip={
                      <Tooltip
                        footer={
                          <List>
                            <li>
                              have a minimum <strong>length of 8 characters</strong>
                            </li>
                            <li>
                              contain at least <strong>one digit</strong>
                            </li>
                            <li>
                              contain at least <strong>one upper-case character</strong>
                            </li>
                            <li>
                              contain at least <strong>one lower-case character</strong>
                            </li>
                          </List>
                        }
                      >
                        Your password must…
                      </Tooltip>
                    }
                  >
                    <LinkButton>Show password requirements</LinkButton>
                  </TooltipHandler>
                }
              >
                <PasswordInputField
                  name="password"
                  placeholder="New password"
                  autoComplete="new-password"
                  appearance={
                    formikForcePassword.submitCount > 0 && !!formikForcePassword.errors.password
                      ? 'error'
                      : undefined
                  }
                  onChange={(v, n, event) => formikForcePassword.handleChange(event)}
                  value={formikForcePassword.values.password}
                  onBlur={formikForcePassword.handleBlur}
                  disabled={formikForcePassword.isSubmitting}
                />
              </FieldNote>
            </FormRow>
            <FormRow>
              <PasswordInputField
                name="confirmPassword"
                placeholder="Repeat password"
                autoComplete="off"
                appearance={
                  formikForcePassword.submitCount > 0 &&
                  !!formikForcePassword.errors.confirmPassword
                    ? 'error'
                    : undefined
                }
                onChange={(v, n, event) => formikForcePassword.handleChange(event)}
                value={formikForcePassword.values.confirmPassword}
                onBlur={formikForcePassword.handleBlur}
                disabled={formikForcePassword.isSubmitting}
              />
            </FormRow>
          </form>
          <Button
            block
            disabled={
              formikForcePassword.isSubmitting ||
              (!isEmpty(formikForcePassword.errors) && formikForcePassword.submitCount > 0)
            }
            onClick={preventDoubleClick(() => formikForcePassword.handleSubmit())}
          >
            Continue
          </Button>
        </>
      )}
    </AuthLayoutPartial>
  )
}

export default SignInPage
